import React, { useCallback, useEffect, useState } from "react";
import SimpleBar from "simplebar-react";
import "simplebar-react/dist/simplebar.min.css";
import {
    createNews,
    deleteNews,
    getNewsById,
    getNewsList,
    updateNews,
} from "../../api/services/newsService";
import {
    buildNewsFormData,
    resolveMediaUrl,
    toDatetimeLocalValue,
} from "../../utils/newsUtils";

const LIMIT = 20;

const EMPTY_FORM = {
    description: "",
    link_url: "",
    dateTime: "",
    image: null,
    imagePreview: "",
};

const AdminNewsPage = () => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [page, setPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);

    const [formOpen, setFormOpen] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(EMPTY_FORM);
    const [saving, setSaving] = useState(false);
    const [formError, setFormError] = useState("");

    const hasMore = page * LIMIT < totalCount;

    const loadList = useCallback(async (pageNum = 1, append = false) => {
        setLoading(true);
        setError("");
        try {
            const { items: nextItems, totalCount: total } = await getNewsList(
                pageNum,
                LIMIT,
            );
            setItems((prev) => (append ? [...prev, ...nextItems] : nextItems));
            setPage(pageNum);
            setTotalCount(total);
        } catch (err) {
            setError(err.message || "Не удалось загрузить новости");
            if (!append) {
                setItems([]);
            }
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadList(1, false);
    }, [loadList]);

    const openCreate = () => {
        setEditingId(null);
        setForm(EMPTY_FORM);
        setFormError("");
        setFormOpen(true);
    };

    const openEdit = async (id) => {
        setFormError("");
        setFormOpen(true);
        setEditingId(id);
        try {
            const item = await getNewsById(id);
            setForm({
                description: item.description ?? "",
                link_url: item.link_url ?? "",
                dateTime: toDatetimeLocalValue(item.dateTime),
                image: null,
                imagePreview: resolveMediaUrl(item.image_url),
            });
        } catch (err) {
            setFormError(err.message || "Не удалось загрузить новость");
        }
    };

    const closeForm = () => {
        setFormOpen(false);
        setEditingId(null);
        setForm(EMPTY_FORM);
        setFormError("");
    };

    const handleImageChange = (event) => {
        const file = event.target.files?.[0];
        if (!file) {
            return;
        }
        setForm((prev) => ({
            ...prev,
            image: file,
            imagePreview: URL.createObjectURL(file),
        }));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setFormError("");

        if (!form.description.trim() || !form.link_url.trim() || !form.dateTime) {
            setFormError("Заполните дату, описание и ссылку");
            return;
        }
        if (!editingId && !form.image) {
            setFormError("Добавьте изображение");
            return;
        }

        const formData = buildNewsFormData({
            image: form.image,
            dateTime: form.dateTime,
            description: form.description.trim(),
            link_url: form.link_url.trim(),
        });

        setSaving(true);
        try {
            if (editingId) {
                await updateNews(editingId, formData);
            } else {
                await createNews(formData);
            }
            closeForm();
            await loadList(1, false);
        } catch (err) {
            setFormError(err.message || "Ошибка сохранения");
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Удалить эту новость?")) {
            return;
        }
        try {
            await deleteNews(id);
            if (editingId === id) {
                closeForm();
            }
            await loadList(1, false);
        } catch (err) {
            setError(err.message || "Не удалось удалить");
        }
    };

    const formatListDate = (dateTime) => {
        if (!dateTime) {
            return "—";
        }
        return new Date(dateTime).toLocaleString("ru-RU", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    };

    return (
        <div className="admin-page admin-news">
            <div className="admin-news__toolbar">
                <h1 className="acp-title admin-news__title">Новости</h1>
                <button
                    type="button"
                    className="ucp-simple-yellow-button admin-news__create-btn"
                    onClick={openCreate}
                >
                    Добавить новость
                </button>
            </div>

            {error && <p className="admin-news__error">{error}</p>}

            <SimpleBar className="admin-dashboard-simplebar">
                <div className="admin-table admin-news__table">
                    <table>
                        <thead>
                            <tr>
                                <th className="admin-table__col-avatar"></th>
                                <th>Дата</th>
                                <th>Описание</th>
                                <th>Ссылка</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && items.length === 0 ? (
                                <tr className="admin-table__empty">
                                    <td colSpan={5}>Загрузка…</td>
                                </tr>
                            ) : items.length === 0 ? (
                                <tr className="admin-table__empty">
                                    <td colSpan={5}>Новостей пока нет</td>
                                </tr>
                            ) : (
                                items.map((item) => (
                                    <tr key={item.id} className="admin-news__row">
                                        <td className="admin-table__cell-avatar">
                                            <img
                                                className="admin-table__avatar admin-news__thumb"
                                                src={resolveMediaUrl(item.image_url)}
                                                alt=""
                                            />
                                        </td>
                                        <td>{formatListDate(item.dateTime)}</td>
                                        <td className="admin-news__title-cell">
                                            {item.description}
                                        </td>
                                        <td className="admin-news__link-cell">
                                            <a
                                                href={item.link_url}
                                                target="_blank"
                                                rel="noreferrer"
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                {item.link_url}
                                            </a>
                                        </td>
                                        <td className="admin-news__actions">
                                            <button
                                                type="button"
                                                className="admin-news__action-btn"
                                                onClick={() => openEdit(item.id)}
                                            >
                                                Изменить
                                            </button>
                                            <button
                                                type="button"
                                                className="admin-news__action-btn admin-news__action-btn--danger"
                                                onClick={() => handleDelete(item.id)}
                                            >
                                                Удалить
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {hasMore && (
                    <button
                        type="button"
                        className="ucp-simple-yellow-button admin-news__load-more"
                        disabled={loading}
                        onClick={() => loadList(page + 1, true)}
                    >
                        Загрузить ещё
                    </button>
                )}
            </SimpleBar>

            {formOpen && (
                <div
                    className="admin-news-form-overlay"
                    role="dialog"
                    aria-modal="true"
                    onClick={closeForm}
                >
                    <form
                        className="admin-news-form"
                        onSubmit={handleSubmit}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h2 className="admin-news-form__heading">
                            {editingId ? "Редактирование" : "Новая новость"}
                        </h2>

                        {formError && (
                            <p className="admin-news__error">{formError}</p>
                        )}

                        <label className="admin-news-form__field">
                            <span>Изображение{editingId ? " (необязательно)" : ""}</span>
                            <input
                                type="file"
                                accept="image/jpeg,image/png,image/webp"
                                onChange={handleImageChange}
                            />
                            {form.imagePreview && (
                                <img
                                    className="admin-news-form__preview"
                                    src={form.imagePreview}
                                    alt=""
                                />
                            )}
                        </label>

                        <label className="admin-news-form__field">
                            <span>Дата и время</span>
                            <input
                                type="datetime-local"
                                name="dateTime"
                                required
                                value={form.dateTime}
                                onChange={(e) =>
                                    setForm((prev) => ({
                                        ...prev,
                                        dateTime: e.target.value,
                                    }))
                                }
                            />
                        </label>

                        <label className="admin-news-form__field">
                            <span>Описание</span>
                            <textarea
                                name="description"
                                required
                                rows={4}
                                value={form.description}
                                onChange={(e) =>
                                    setForm((prev) => ({
                                        ...prev,
                                        description: e.target.value,
                                    }))
                                }
                            />
                        </label>

                        <label className="admin-news-form__field">
                            <span>Ссылка</span>
                            <input
                                type="url"
                                name="link_url"
                                required
                                placeholder="https://"
                                value={form.link_url}
                                onChange={(e) =>
                                    setForm((prev) => ({
                                        ...prev,
                                        link_url: e.target.value,
                                    }))
                                }
                            />
                        </label>

                        <div className="admin-news-form__buttons">
                            <button
                                type="button"
                                className="admin-news-form__cancel"
                                onClick={closeForm}
                                disabled={saving}
                            >
                                Отмена
                            </button>
                            <button
                                type="submit"
                                className="ucp-simple-yellow-button"
                                disabled={saving}
                            >
                                {saving ? "Сохранение…" : "Сохранить"}
                            </button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
};

export default AdminNewsPage;
